import { Icon } from './Icon';

interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  pageSizeOptions?: number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  /** Optional summary slot rendered on the left, e.g. "12 selected". */
  summary?: React.ReactNode;
}

export function Pagination({
  page,
  pageSize,
  total,
  pageSizeOptions = [25, 50, 100, 500, 1000, 10000],
  onPageChange,
  onPageSizeChange,
  summary,
}: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : page * pageSize + 1;
  const end = Math.min(total, (page + 1) * pageSize);

  return (
    <div className="table-foot">
      {summary ?? (
        <span>
          {total === 0 ? '0 results' : `${start}–${end} of ${total.toLocaleString()}`}
        </span>
      )}
      <span className="page-size">
        Rows per page
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
        >
          {pageSizeOptions.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </span>
      <span className="page-nav">
        <button
          type="button"
          className="pn-btn"
          onClick={() => onPageChange(0)}
          disabled={page === 0}
          aria-label="First page"
        >
          <Icon name="chevronsLeft" size={12} />
        </button>
        <button
          type="button"
          className="pn-btn"
          onClick={() => onPageChange(page - 1)}
          disabled={page === 0}
          aria-label="Previous page"
        >
          <Icon name="chevronLeft" size={12} />
        </button>
        <span style={{ padding: '0 6px' }}>
          {page + 1} / {totalPages}
        </span>
        <button
          type="button"
          className="pn-btn"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages - 1}
          aria-label="Next page"
        >
          <Icon name="chevronRight" size={12} />
        </button>
        <button
          type="button"
          className="pn-btn"
          onClick={() => onPageChange(totalPages - 1)}
          disabled={page >= totalPages - 1}
          aria-label="Last page"
        >
          <Icon name="chevronsRight" size={12} />
        </button>
      </span>
    </div>
  );
}
